import React, { useMemo } from 'react';

import { Modal } from 'react-bootstrap';
import pluralize from 'pluralize';
import MilestoneCard from '../../MilestoneCard.jsx';
import { getOrgTermsContent } from '../../../content/utils.js';
import { PortfolioInfoLink, ProjectLink } from '../../links/index.jsx';

const MilestonesModal = ({
  onHide,
  show,
  milestones,
  projects,
  portfolios,
  slug,
}) => {
  const uniqueIds = (items, key) => [
    ...new Set(items.filter((item) => item[key]).map((item) => item[key])),
  ];

  const uniqueProjectIds = useMemo(
    () => uniqueIds(milestones, 'project_id'),
    [milestones]
  );
  const uniquePortfolioIds = useMemo(
    () => uniqueIds(milestones, 'portfolio_id'),
    [milestones]
  );
  const getData2 = () => {
    const totalPortfolioIds = Array.from(
      new Set([
        ...uniqueIds(
          projects.filter((p) => uniqueProjectIds.includes(p.id)),
          'portfolioId'
        ),
        ...uniquePortfolioIds,
      ])
    );

    const portfolioEntities = portfolios.filter((p) =>
      totalPortfolioIds.includes(p.id)
    );
    const coveredProjectIds = portfolioEntities.flatMap((p) =>
      p.projects.map((proj) => proj.id)
    );
    const coveredMilestonePortfolioIds = portfolioEntities.map((p) => p.id);

    const noPortfolioProjects = projects
      .filter(
        (p) =>
          uniqueProjectIds.includes(p.id) &&
          !coveredProjectIds.includes(p.id) &&
          !p.portfolioId
      )
      .map((p) => ({
        id: p.id,
        name: p.name,
        milestones: milestones.filter((m) => m.project_id === p.id),
      }));

    const noPortfolioDirectMilestones = milestones.filter(
      (m) =>
        !m.portfolio_id &&
        !m.project_id &&
        !coveredMilestonePortfolioIds.includes(m.portfolio_id)
    );
    const noPortfolioEntry =
      noPortfolioProjects.length || noPortfolioDirectMilestones.length
        ? [
            {
              id: 'no_portfolio',
              name: '—',
              milestones: noPortfolioDirectMilestones,
              projects: noPortfolioProjects,
            },
          ]
        : [];

    return [
      ...portfolioEntities.map((p) => {
        return {
          id: p.id,
          name: p.name || '—',
          milestones: milestones.filter((m) => m.portfolio_id === p.id),
          projects: [
            ...p.projects
              .map((p) => p.id)
              .filter((id) => uniqueProjectIds.includes(id))
              .map((id) => {
                const projectEntity = projects.find((p) => p.id === id);
                return {
                  id,
                  name: projectEntity.name,
                  milestones: milestones.filter((m) => m.project_id === id),
                };
              }),
          ],
        };
      }),
      ...noPortfolioEntry,
    ].filter((e) => e.milestones.length || e.projects.length);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontWeight: 400 }}>
          <div className="flex">
            <span>{pluralize('Milestone', milestones.length)}</span>
            <strong className="bordered text-muted m-l-16">
              Total:&nbsp;
              <span className="text-primary count">{milestones.length}</span>
            </strong>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {getData2().map((portfolio, index) => {
          if (!(portfolio.projects.length || portfolio.milestones.length))
            return null;
          return (
            <React.Fragment>
              <div key={portfolio.id} className="m-b-32">
                <h4 className="flex m-b-20">
                  <span className="text-muted">
                    {getOrgTermsContent('portfolio')}:&nbsp;
                  </span>
                  {portfolio.id !== 'no_portfolio' ? (
                    <PortfolioInfoLink portfolioId={portfolio.id}>
                      {portfolio.name}
                    </PortfolioInfoLink>
                  ) : (
                    portfolio.name
                  )}
                </h4>
                <div className="m-l-18">
                  {portfolio.milestones.map((milestone) => (
                    <React.Fragment key={milestone.id}>
                      <MilestoneCard mile={milestone} />
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {portfolio.projects.map((project) => (
                <div key={project.id} className="m-b-32">
                  <h4 className="flex m-b-20">
                    <span className="text-muted">
                      {getOrgTermsContent('project')}:&nbsp;
                    </span>
                    <ProjectLink projectId={project.id}>
                      {project.name}
                    </ProjectLink>
                  </h4>
                  <div className="m-l-18">
                    {project.milestones.map((milestone) => (
                      <React.Fragment key={milestone.id}>
                        <MilestoneCard mile={milestone} />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
              {index + 1 !== getData2().length && <hr className="m-b-32" />}
            </React.Fragment>
          );
        })}
      </Modal.Body>
    </Modal>
  );
};

export default MilestonesModal;
